import { CollapsibleContent, LabeledContainer } from '@schibsted-svp/react-ui';
import type { Asset } from '@schibsted-svp/svp-api-types';
import { useEffect, type ReactNode, useMemo } from 'react';
import * as assetModel from 'models/asset';
import * as multiLiveEventModel from 'models/multiLiveEvent';
import { useLazyLiveStreamQuery } from 'services/admin-bff-sdk/generated';
import { FaCheck } from 'react-icons/fa';
import css from './StreamDetails.module.scss';

function dvrLengthToHumanReadable(seconds: number) {
    if (!Number.isFinite(seconds)) {
        return '';
    }
    if (seconds >= 3600) {
        const hours = Math.floor(seconds / 3600);
        return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
    if (seconds >= 0) {
        return 'No DVR';
    }
    if (seconds === -1) {
        return 'Unlimited';
    }
    return '';
}

interface StreamDetailsProps {
    asset: Asset;
    divider: ReactNode;
}

type CheckboxStreamDetail = {
    type: 'checkbox';
    label: string;
};

type TextStreamDetail = {
    type: 'text';
    label: string;
    value: string;
};

type StreamDetail = CheckboxStreamDetail | TextStreamDetail;

export function StreamDetails({ asset, divider }: StreamDetailsProps) {
    const [getLiveStream, { data, isUninitialized }] = useLazyLiveStreamQuery();
    const { provider: newsroom, id: assetId } = asset;
    const isLive = assetModel.isLive(asset);
    const isHlsProtocol = assetModel.isHLSProtocol(asset);
    const dvrLength =
        // eslint-disable-next-line no-underscore-dangle
        data?.live.__typename === 'ElementalLive' ? multiLiveEventModel.getDvrLength(data.live.events) : undefined;
    const hasWscRtmpOutput =
        // eslint-disable-next-line no-underscore-dangle
        data?.live.__typename === 'ElementalLive' ? multiLiveEventModel.getWscRtmpOutput(data.live.events) : undefined;
    const audioChannelsMixLabel =
        // eslint-disable-next-line no-underscore-dangle
        data?.live.__typename === 'ElementalLive'
            ? multiLiveEventModel.getAudioChannelsMixLabel(multiLiveEventModel.getAudioChannelsMix(data.live.events))
            : undefined;

    const details = useMemo(() => {
        const values: StreamDetail[] = [];

        if (dvrLength) {
            values.push({ type: 'text', label: 'DVR Length', value: dvrLengthToHumanReadable(dvrLength) });
        }

        if (hasWscRtmpOutput) {
            values.push({ type: 'checkbox', label: 'Send to WSC Sports' });
        }

        if (audioChannelsMixLabel) {
            values.push({ type: 'text', label: 'Audio channels', value: audioChannelsMixLabel });
        }

        return values;
    }, [dvrLength, hasWscRtmpOutput, audioChannelsMixLabel]);

    useEffect(() => {
        if (isLive && isHlsProtocol) {
            getLiveStream({ newsroom, assetId });
        }
    }, [assetId, newsroom, isLive, isHlsProtocol, getLiveStream]);

    if (isUninitialized || !details.length) {
        return null;
    }

    return (
        <>
            <CollapsibleContent label="Stream details" startExpanded>
                {details.map((detail) => (
                    <LabeledContainer
                        key={detail.label}
                        label={`• ${detail.label}:`}
                        variant="horizontal"
                        horizontalLabelSpace="compact"
                        className={css.streamDetail}
                    >
                        {detail.type === 'checkbox' ? <FaCheck className={css.checkmark} /> : detail.value}
                    </LabeledContainer>
                ))}
            </CollapsibleContent>
            {divider}
        </>
    );
}
